/**
 * Procès-verbal (SPECIFICATION.md §6.7).
 *
 * Le PV montre **toujours** côte à côte le résultat calculé et le résultat
 * retenu : une décision souveraine de l'enseignant ne doit jamais pouvoir se
 * confondre avec le dépouillement.
 */

import { personLabel, retainedSeats, type Computation, type ElectionState } from './state.js';
import { PHASE_LABEL, type Seat, type SeatRole } from './types.js';

export const REFERENCE_F1370 = 'service-public.gouv.fr — Délégués de classe (F1370)';

/**
 * Accord du nom après un nombre. En français, 0 et 1 prennent le singulier.
 * `pluriel` de l'interface fait le même travail ; le noyau reste sans dépendance.
 */
function accord(nombre: number, singulier: string, plurielMot: string): string {
  return `${nombre} ${nombre > 1 ? plurielMot : singulier}`;
}

const ROLE_LABEL: Record<SeatRole, string> = {
  titulaire: 'Délégué titulaire',
  suppleant: 'Suppléant',
};

export interface PVSeat {
  readonly role: string;
  readonly personne: string;
  readonly origine: string;
}

export interface PVChiffres {
  readonly inscrits: number;
  readonly votants: number;
  readonly valides: number;
  readonly complets: number;
  readonly partiels: number;
  readonly blancs: number;
  readonly nuls: number;
  readonly participation: string;
}

export interface PVTour {
  readonly titre: string;
  readonly voixParElecteur: number;
  readonly seuil: string;
  readonly classement: readonly { label: string; voix: number; majoritaire: boolean }[];
  readonly chiffres: PVChiffres;
  readonly nulsParMotif: readonly { motif: string; nombre: number }[];
}

export interface PVModel {
  readonly nom: string;
  readonly classe: string;
  readonly etablissement: string;
  readonly date: string;
  readonly reference: string;
  readonly phase: string;
  readonly purgeLe: string;
  readonly creeLe: string;
  readonly empreinte: string;
  readonly empreinteJournal: string;
  readonly tours: readonly PVTour[];
  readonly siegeCalcules: readonly PVSeat[];
  readonly siegeRetenus: readonly PVSeat[];
  readonly ecart: boolean;
  readonly decisions: readonly string[];
  readonly designations: readonly string[];
  readonly mandats: readonly string[];
  readonly departages: readonly string[];
  readonly egaliteEnCours: readonly string[];
  readonly incidents: readonly string[];
}

const ORIGINE_LABEL: Record<string, string> = {
  elu: 'élu au scrutin',
  designe: 'désigné après le scrutin',
  force: 'décision de l’enseignant',
};

function formatDate(value: Date): string {
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' }).format(value);
}

function formatJour(value: Date): string {
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(value);
}

/**
 * Date de purge : J+7 après l'élection si elle a eu lieu, J+7 après création
 * sinon (D13). Rien n'est conservé au-delà.
 */
export function purgeDate(state: ElectionState): Date {
  const tenue = state.phase === 'PROCLAMATION' && state.config !== null;
  const base = tenue && state.config !== null
    ? new Date(`${state.config.dateISO}T23:59:59`)
    : new Date(state.events[0]?.ts ?? Date.now());
  return new Date(base.getTime() + 7 * 24 * 3600 * 1000);
}

function chiffresDe(result: Computation['round1']): PVChiffres {
  const participation = result.inscrits === 0 ? '—' : `${Math.round((result.votants / result.inscrits) * 100)} %`;
  return {
    inscrits: result.inscrits,
    votants: result.votants,
    valides: result.valides,
    complets: result.complets,
    partiels: result.partiels,
    blancs: result.blancs,
    nuls: result.nuls,
    participation,
  };
}

function seatView(state: ElectionState, seat: Seat): PVSeat {
  return {
    role: ROLE_LABEL[seat.role],
    personne: personLabel(state, seat.personId),
    origine: ORIGINE_LABEL[seat.via] ?? seat.via,
  };
}

/** Construit le modèle du procès-verbal, imprimable et exportable. */
export function buildPV(state: ElectionState, computation: Computation): PVModel {
  const retenus = retainedSeats(state, computation);

  const tourView = (result: Computation['round1'], titre: string): PVTour => ({
    titre,
    voixParElecteur: result.voixParElecteur,
    seuil:
      result.seuil === null
        ? 'majorité relative'
        : `${accord(result.seuil, 'voix', 'voix')} (majorité absolue sur ${accord(result.valides, 'bulletin valide', 'bulletins valides')})`,
    classement: result.classement.map((entry) => ({
      label: entry.label,
      voix: entry.voix,
      majoritaire: result.majoritaires.includes(entry.itemId),
    })),
    chiffres: chiffresDe(result),
    nulsParMotif: Object.entries(result.nulsParMotif).map(([motif, nombre]) => ({ motif, nombre })),
  });

  const tours: PVTour[] = [tourView(computation.round1, 'Premier tour')];
  // Un second tour sans aucun bulletin n'a pas eu lieu : il ne figure pas au PV.
  if (computation.round2 && computation.round2.valides + computation.round2.blancs + computation.round2.nuls > 0) {
    tours.push(tourView(computation.round2, 'Second tour'));
  }

  const empreinteJournal = state.events.length > 0 ? (state.events[state.events.length - 1] as { hash: string }).hash : '—';

  const calcule = computation.siegesCalcules.map((seat) => seatView(state, seat));
  const retenu = retenus.map((seat) => seatView(state, seat));
  const ecart =
    calcule.length !== retenu.length ||
    calcule.some((seat, index) => {
      const autre = retenu[index];
      return autre === undefined || seat.role !== autre.role || seat.personne !== autre.personne;
    });

  return {
    nom: state.config?.nom ?? 'Élection sans nom',
    classe: state.config?.classe ?? '—',
    etablissement: state.config?.etablissement ?? '—',
    date: state.config?.dateISO ?? '—',
    reference: REFERENCE_F1370,
    phase: PHASE_LABEL[state.phase],
    purgeLe: formatJour(purgeDate(state)),
    creeLe: formatDate(new Date(state.events[0]?.ts ?? Date.now())),
    empreinte: computation.empreinte,
    empreinteJournal,
    tours,
    siegeCalcules: calcule,
    siegeRetenus: retenu,
    ecart,
    decisions: [...state.validations, ...state.overrides]
      .sort((a, b) => a.ts - b.ts)
      .map(
        (acte) =>
          `${formatDate(new Date(acte.ts))} — ${acte.kind === 'validation' ? 'validation' : 'MODIFICATION'} du résultat (phase ${PHASE_LABEL[acte.phase]}) : ${acte.motif || 'sans motif'}`,
      ),
    designations: state.designations.map(
      (entry) =>
        `${personLabel(state, entry.titulaireId)} désigne ${personLabel(state, entry.suppleantId)} comme suppléant${entry.motif ? ` — ${entry.motif}` : ''}`,
    ),
    mandats: state.mandates.map(
      (entry) =>
        `${personLabel(state, entry.personId)} : mandat ${entry.accepted ? 'accepté' : 'REFUSÉ'}${entry.motif ? ` — ${entry.motif}` : ''}`,
    ),
    departages: state.tiebreaks.map(
      (entry) =>
        `Tour ${entry.round} : égalité entre ${entry.tiedItemIds.length} items, retenu par l’enseignant — ${entry.motif}`,
    ),
    egaliteEnCours:
      computation.ambigu === null
        ? []
        : computation.ambigu.itemIds.map((itemId) => computation.items.find((item) => item.id === itemId)?.label ?? itemId),
    incidents: state.incidents,
  };
}

/** Rendu texte du PV, pour l'impression et l'export. */
export function pvToText(pv: PVModel): string {
  const lignes: string[] = [];
  lignes.push(`PROCÈS-VERBAL — ${pv.nom}`);
  lignes.push(`Classe : ${pv.classe}   Établissement : ${pv.etablissement}`);
  lignes.push(`Date de l'élection : ${pv.date}   Référence : ${pv.reference}`);
  lignes.push(`Phase : ${pv.phase}`);
  lignes.push('');
  for (const tour of pv.tours) {
    lignes.push(`${tour.titre.toUpperCase()} — ${tour.voixParElecteur} voix par électeur, ${tour.seuil}`);
    lignes.push(
      `  Inscrits ${tour.chiffres.inscrits} · Votants ${tour.chiffres.votants} · Participation ${tour.chiffres.participation} · Valides ${tour.chiffres.valides} (dont ${tour.chiffres.complets} complets, ${tour.chiffres.partiels} partiels) · Blancs ${tour.chiffres.blancs} · Nuls ${tour.chiffres.nuls}`,
    );    for (const entry of tour.classement) {
      lignes.push(`  ${entry.voix.toString().padStart(4)}  ${entry.label}${entry.majoritaire ? '  [majorité absolue]' : ''}`);
    }
    if (tour.nulsParMotif.length > 0) {
      lignes.push(`  Bulletins nuls : ${tour.nulsParMotif.map((entry) => `${entry.motif} ×${entry.nombre}`).join(', ')}`);
    }
    lignes.push('');
  }
  lignes.push('RÉSULTAT CALCULÉ');
  for (const seat of pv.siegeCalcules) lignes.push(`  ${seat.role} : ${seat.personne} (${seat.origine})`);
  lignes.push('');
  lignes.push('RÉSULTAT RETENU');
  for (const seat of pv.siegeRetenus) lignes.push(`  ${seat.role} : ${seat.personne} (${seat.origine})`);
  if (pv.ecart) lignes.push("  ⚠ ÉCART entre le calcul et le résultat retenu : voir les décisions souveraines.");
  if (pv.egaliteEnCours.length > 0) lignes.push(`  ⚠ Égalité non tranchée : ${pv.egaliteEnCours.join(', ')}`);
  lignes.push('');
  if (pv.decisions.length > 0) {
    lignes.push('DÉCISIONS SOUVERAINES DE L’ENSEIGNANT');
    for (const decision of pv.decisions) lignes.push(`  ${decision}`);
    lignes.push('');
  }
  if (pv.designations.length > 0) {
    lignes.push('DÉSIGNATIONS DE SUPPLÉANT');
    for (const entry of pv.designations) lignes.push(`  ${entry}`);
    lignes.push('');
  }
  if (pv.mandats.length > 0) {
    lignes.push('ACCEPTATION DES MANDATS');
    for (const entry of pv.mandats) lignes.push(`  ${entry}`);
    lignes.push('');
  }
  if (pv.departages.length > 0) {
    lignes.push('DÉPARTAGES');
    for (const entry of pv.departages) lignes.push(`  ${entry}`);
    lignes.push('');
  }
  lignes.push(`Empreinte du résultat : ${pv.empreinte}`);
  lignes.push(`Empreinte du journal : ${pv.empreinteJournal}`);
  lignes.push(`Créée le ${pv.creeLe} — données supprimées après le ${pv.purgeLe}`);
  return lignes.join('\n');
}
